import React, { useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  PageContent,
  Container,
  Row,
  Col,
  Field,
  Heading,
  Button,
  Select,
  Datepicker,
} from 'components';
import { globalPropTypes } from 'helpers';
import moment from 'moment';
import { Margin } from 'styled-components-spacing';
import clearErrors from 'helpers/clearErrors';
import {
  deltaClassOptions,
  deltaCertificatesTypeOptions,
} from '../../../../../shared/Cis/CisOptions';
import { connect } from 'react-redux';
import editReducer, { defaultState, setEditDataCreator, updateFieldCreator } from './reducer';
import {
  loadGlobalCisDeltaCertificatesAttributesCreator,
  setIpmaCisEditErrorsCreator,
  updateIpmaCisCreator,
} from '../../../../../store/actionsCreators';

function EditIpmaDeltaCertificate({
  deltaCertificate,
  errors,
  updateDeltaCertificate,
  removeError,
  organizations,
  loadDeltaCertificatesAttributes,
}) {
  const { t } = useTranslation();
  const history = useHistory();
  const [state, dispatch] = useReducer(editReducer, defaultState);

  const handleChangeNative = (name, errorName) => (e) => {
    dispatch(updateFieldCreator({ name, value: e.target.value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleChangeCustom = (name, errorName) => (value) => {
    dispatch(updateFieldCreator({ name, value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleChangeSelect = (name, errorName) => (option) => {
    dispatch(updateFieldCreator({ name, value: option?.value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const submit = () => {
    const params = {
      certId: state.certId,
      deltaClass: state.deltaClass,
      status: state.status,
      organization: state.organization,
      validTill: state.validTill ? moment(state.validTill).format('YYYY-MM-DD') : null,
      dateDeltaCertificate: state.dateDeltaCertificate
        ? moment(state.dateDeltaCertificate).format('YYYY-MM-DD')
        : null,
    };

    updateDeltaCertificate(state.id, params, history);
  };

  useEffect(() => {
    loadDeltaCertificatesAttributes();
  }, []);

  useEffect(() => {
    if (deltaCertificate) {
      dispatch(setEditDataCreator(deltaCertificate));
    }
  }, [deltaCertificate]);

  return (
    <PageContent>
      <Container>
        <form>
          <Margin bottom="24">
            <Heading size="h5">{t('page.user.cis.deltaCertificates.editDeltaCertificate')}</Heading>
          </Margin>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="certId"
                  label={t('page.user.cis.deltaCertificates.certId')}
                  required
                  error={errors.certId}
                >
                  <input
                    id="certId"
                    type="text"
                    placeholder={t('page.user.cis.deltaCertificates.certIdPlaceholder')}
                    value={state.certId}
                    onChange={handleChangeNative('certId', 'certId')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="status"
                  label={t('page.user.cis.deltaCertificates.status')}
                  required
                  error={errors.status}
                >
                  <Select
                    id="status"
                    type="text"
                    placeholder={t('page.user.cis.deltaCertificates.statusPlaceholder')}
                    value={
                      deltaCertificatesTypeOptions.find(
                        (option) => option.value === state.status,
                      ) || ''
                    }
                    options={deltaCertificatesTypeOptions}
                    onChange={handleChangeSelect('status', 'status')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="deltaClass"
                  label={t('page.user.cis.deltaCertificates.deltaClass')}
                  required
                  error={errors.deltaClass}
                >
                  <Select
                    id="deltaClass"
                    type="text"
                    placeholder={t('page.user.cis.deltaCertificates.deltaClassPlaceholder')}
                    value={
                      deltaClassOptions.find((option) => option.value === state.deltaClass) || ''
                    }
                    options={deltaClassOptions}
                    onChange={handleChangeSelect('deltaClass', 'deltaClass')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="dateDeltaCertificate"
                  label={t('page.user.cis.deltaCertificates.dateDeltaCertificate')}
                  value={state.dateDeltaCertificate}
                  clear={() => handleChangeCustom('dateDeltaCertificate')(null)}
                  required
                  error={errors.dateDeltaCertificate}
                >
                  <Datepicker
                    id="dateDeltaCertificate"
                    small
                    placeholderText={t(
                      'page.user.cis.deltaCertificates.dateDeltaCertificatePlaceholder',
                    )}
                    selected={state.dateDeltaCertificate}
                    onChange={handleChangeCustom('dateDeltaCertificate', 'dateDeltaCertificate')}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    showMonthDropdown
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="validTill"
                  label={t('page.user.cis.deltaCertificates.validTill')}
                  value={state.validTill}
                  clear={() => handleChangeCustom('validTill')(null)}
                  required
                  error={errors.validTill}
                >
                  <Datepicker
                    id="validTill"
                    small
                    placeholderText={t('page.user.cis.deltaCertificates.validTillPlaceholder')}
                    selected={state.validTill}
                    onChange={handleChangeCustom('validTill', 'validTill')}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    showMonthDropdown
                    minDate={new Date(state.dateDeltaCertificate)}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="organization"
                  label={t('page.user.cis.deltaCertificates.organization')}
                  value={organizations.find((option) => option.value === state.organization) || ''}
                  clear={() => handleChangeSelect('organization')(null)}
                  required
                  error={errors.organization}
                >
                  <Select
                    id="organization"
                    type="text"
                    placeholder={t('page.user.cis.deltaCertificates.organizationPlaceholder')}
                    value={
                      organizations.find((option) => option.value === state.organization) || ''
                    }
                    options={organizations}
                    onChange={handleChangeSelect('organization', 'organization')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom={{ xs: 12, md: 0 }}>
                <Button type="button" onClick={() => submit()}>
                  {t('global.saveChangesButton')}
                </Button>
              </Margin>
            </Col>
          </Row>
        </form>
      </Container>
    </PageContent>
  );
}

EditIpmaDeltaCertificate.propTypes = {
  deltaCertificate: PropTypes.shape({
    id: PropTypes.number,
    certId: PropTypes.number,
    status: PropTypes.string,
    deltaClass: PropTypes.number,
    dateDeltaCertificate: PropTypes.any,
    validTill: PropTypes.any,
    organization: PropTypes.number,
  }),
  errors: PropTypes.shape({
    certId: PropTypes.string,
    status: PropTypes.string,
    deltaClass: PropTypes.string,
    dateDeltaCertificate: PropTypes.string,
    validTill: PropTypes.string,
    organization: PropTypes.string,
  }),
  updateDeltaCertificate: PropTypes.func.isRequired,
  removeError: PropTypes.func.isRequired,
  organizations: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  loadDeltaCertificatesAttributes: PropTypes.func.isRequired,
};

EditIpmaDeltaCertificate.defaultProps = {
  certificate: null,
  errors: {},
};

const mapStateToProps = ({ ipma, globals }) => ({
  deltaCertificate: ipma.cis.deltaCertificates.profile,
  errors: ipma.cis.deltaCertificates.editErrors,
  organizations: globals.cis.organizations,
});

const mapDispatchToProps = (dispatch) => ({
  updateDeltaCertificate: (id, payload, history) =>
    dispatch(updateIpmaCisCreator(id, 'deltaCertificates', payload, history)),
  removeError: (payload) => dispatch(setIpmaCisEditErrorsCreator('deltaCertificates', payload)),
  loadDeltaCertificatesAttributes: () =>
    dispatch(loadGlobalCisDeltaCertificatesAttributesCreator()),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditIpmaDeltaCertificate);
